import { Box, Tooltip, Typography } from "@mui/material";
import { useCallback, useContext } from "react";
import SnackbarContext from "../../../../contexts/SnackbarContext";

type ColorSwatchProps = {
  color?: string | null;
  label?: string;
  size?: "small" | "large";
  copyOnClick?: boolean;
};

const ColorSwatch = ({
  color,
  label,
  size = "small",
  copyOnClick = true,
}: ColorSwatchProps) => {
  const swatchSize = size === "large" ? 60 : 40;
  const isEmpty = !color;
  const snackbarContext = useContext(SnackbarContext);

  const handleClick = useCallback(() => {
    if (!color || !copyOnClick) return;
    navigator.clipboard
      .writeText(color)
      .then(() => {
        snackbarContext?.openSnackbar(
          `${color} copied to clipboard`,
          "success"
        );
      })
      .catch((err) => {
        console.error("Failed to copy color to clipboard:", err);
        snackbarContext?.openSnackbar(
          "Failed to copy color to clipboard",
          "error"
        );
      });
  }, [color, copyOnClick, snackbarContext]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Tooltip
        title={isEmpty ? "Not set" : `${color}`}
        placement="top"
        PopperProps={{
          disablePortal: true,
          popperOptions: {
            strategy: "fixed",
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  enabled: true,
                  boundariesElement: "window",
                },
              },
            ],
          },
        }}
        arrow
      >
        <Box
          onClick={handleClick}
          sx={{
            width: swatchSize,
            height: swatchSize,
            borderRadius: "8px",
            backgroundColor: isEmpty ? "#F5F5F5" : color,
            border: isEmpty
              ? "2px dashed #E0E0E0"
              : color === "#FFFFFF"
              ? "1px solid #E0E0E0"
              : "none",
            boxShadow: isEmpty ? "none" : "0 2px 4px rgba(0,0,0,0.1)",
            cursor: isEmpty ? "default" : "pointer",
            transition: "transform 0.1s ease",
            "&:hover": {
              transform: isEmpty ? "none" : "scale(1.05)",
            },
          }}
        />
      </Tooltip>
      {label && (
        <Typography variant="caption" textAlign="center">
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default ColorSwatch;
